import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { AuthFacade } from '@libs/auth-lib';
import { QuickRegisterRequest } from '@libs/entity-lib';
import { fetchProfile } from '@libs/settings-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, catchError, exhaustMap, from, map, of, switchMap, tap } from 'rxjs';
import { CrudLessorLibService } from '../../services/crud-lessor/crud-lessor-lib.service';
import * as fromActions from './quick-register.actions';
import { State } from './quick-register.reducer';

@Injectable()
export class QuickRegisterEffects {
  private readonly actions$ = inject(Actions);
  private readonly crudLessorLibService = inject(CrudLessorLibService);
  private readonly toastController = inject(ToastController);
  private readonly store = inject(Store);
  private readonly authFacade = inject(AuthFacade);

  registerSportSenter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.quickRegisterProfile),
      // exhaustMap, não switchMap: cadastro é mutação. switchMap cancelaria o
      // request anterior sem desfazer o que o servidor já criou — o segundo
      // toque devolveria "e-mail já cadastrado" sobre o próprio atleta.
      exhaustMap(({ profileState }) => {
        const expectsSession = this.isAthleteSelfRegister(profileState);
        return this.crudLessorLibService.quickCreate(profileState).pipe(
          switchMap(() => (expectsSession ? this.confirmSession() : of(false))),
          map((sessionEstablished) => {
            // O cadastro deu certo nos dois casos — o que muda é o próximo passo
            // do atleta. Sem sessão, dizer "sucesso" e deixá-lo num app que não
            // responde é pior que mandá-lo pro login.
            this.presentToastTop(
              !expectsSession || sessionEstablished
                ? 'Atleta registrado com sucesso!'
                : 'Cadastro concluído! Entre com seu e-mail e senha.',
              2500,
              'success'
            );
            return fromActions.quickRegisterProfileSuccefully({
              newState: {} as State,
              sessionEstablished,
            });
          }),
          catchError((error) => {
            // Preferimos SEMPRE a mensagem do backend quando existir — ela já vem
            // clara e acionável (validação 400, integridade 422/409, regra de negócio).
            // Só caímos no texto genérico quando o servidor não mandou mensagem
            // (ex.: 500 inesperado, timeout, offline).
            const backendMessage = error?.error?.message?.trim();
            const message = backendMessage
              || 'Não foi possível concluir o cadastro agora. Tente novamente em instantes.';

            this.presentToastTop(message, 3500, 'danger');
            return of(fromActions.quickRegisterProfileFailed({ error }));
          })
        );
      })
    )
  );

  /**
   * Confirma a sessão do auto-cadastro VERIFICANDO, não afirmando.
   *
   * O `POST /signup/athlete` já autentica no servidor e devolve o cookie; aqui
   * só perguntamos ao `GET /signin/me` se essa credencial vale. Antes, este
   * effect pegava o `LoginResponse` do CORPO da resposta e escrevia direto no
   * store de auth — mas o app autentica exclusivamente por cookie
   * (`withCredentials`, nenhum interceptor lê `accessToken`), então um corpo sem
   * cookie correspondente produzia sessão fantasma: shell "logado" com
   * `profile`/`status`/`pending` todos em 401 e tela em branco.
   *
   * Verificando, o pior caso vira honesto: `restoreSessionFailed` assenta o
   * estado anônimo (→ `resetProfile`) e o atleta é mandado pro login. E o
   * caminho de sucesso ganha de graça tudo que se pendura em
   * `sessionEstablished` (flag `ss:has-session`, `fetchProfileStatus`).
   */
  private confirmSession(): Observable<boolean> {
    return from(this.authFacade.restoreSession()).pipe(
      tap((established) => {
        if (established) {
          this.store.dispatch(fetchProfile());
        }
      })
    );
  }

  /**
   * Só o auto-cadastro do atleta (define a própria senha) estabelece sessão —
   * inclui o atleta convidado por um CE (`sportCenterId` presente). O fluxo do
   * lessor cadastrando cliente NÃO tem senha (é enviada por email) → sem
   * auto-login, e sem `/signin/me` fantasma na sessão do lessor.
   */
  private isAthleteSelfRegister(profileState: QuickRegisterRequest): boolean {
    return !profileState.sportCenterId || !!profileState.owner?.password;
  }

  async presentToastTop(msg: string, dur: number, col: string) {
    const toast = await this.toastController.create({
      message: msg,
      duration: dur,
      position: 'bottom',
      color: col
    });

    await toast.present();
  }
}
